/**
 * Connector instance wizard page component.
 * Step-by-step wizard for creating connector instances (Zapier-style).
 */

// React Imports
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  LinearProgress,
  Chip,
  TextField,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { connectorService } from '../services/connectorService';
import { useNotificationStore } from '../store/notificationStore';
import { ConnectorIcon } from '../components/connectors/ConnectorIcon';
import { ConnectorTestPanel } from '../components/connectors/ConnectorTestPanel';
import { ConnectorConfigRenderer } from '../components/connectors/ConnectorConfigRenderer';
import { CapabilityList } from '../components/connectors/CapabilityList';
import type {
  ConnectorDetailDto,
  CreateConnectorInstanceRequest,
} from '../types/connector';

const steps = ['Basic Information', 'Authentication', 'Settings & Defaults', 'Capabilities', 'Review & Create'];

/**
 * Connector instance wizard page component.
 */
export const ConnectorInstanceWizardPage = () => {
  const { connectorId } = useParams<{ connectorId: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [connector, setConnector] = useState<ConnectorDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);

  // Form data
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [rateLimitPerMinute, setRateLimitPerMinute] = useState<number | ''>('');
  const [rateLimitPerHour, setRateLimitPerHour] = useState<number | ''>('');
  const [rateLimitPerDay, setRateLimitPerDay] = useState<number | ''>('');
  const [credentialJson, setCredentialJson] = useState('');
  const [settingsJson, setSettingsJson] = useState('{}');
  const [enabledCapabilityIds, setEnabledCapabilityIds] = useState<string[]>([]);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (connectorId) {
      loadConnector();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectorId]);

  const loadConnector = async () => {
    if (!connectorId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await connectorService.getConnector(connectorId);
      setConnector(data);
      // Enable all capabilities by default
      setEnabledCapabilityIds(data.capabilities.filter((c) => c.isActive).map((c) => c.id));
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load connector';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    // Validate current step
    if (activeStep === 0) {
      if (!name.trim()) {
        setError('Instance name is required');
        return;
      }
    }
    if (activeStep === 1) {
      // Authentication validation depends on auth type
      if (connector?.authenticationType === 'OAuth2' && !isConnected) {
        setError('Please connect using OAuth before proceeding');
        return;
      }
    }

    setError(null);
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleToggleCapability = (capabilityId: string, enabled: boolean) => {
    if (enabled) {
      setEnabledCapabilityIds([...enabledCapabilityIds, capabilityId]);
    } else {
      setEnabledCapabilityIds(enabledCapabilityIds.filter((id) => id !== capabilityId));
    }
  };

  const handleCreate = async () => {
    setSaving(true);
    setError(null);
    try {
      const request: CreateConnectorInstanceRequest = {
        name: name.trim(),
        description: description.trim() || undefined,
        isActive,
        rateLimitPerMinute: rateLimitPerMinute ? Number(rateLimitPerMinute) : undefined,
        rateLimitPerHour: rateLimitPerHour ? Number(rateLimitPerHour) : undefined,
        rateLimitPerDay: rateLimitPerDay ? Number(rateLimitPerDay) : undefined,
        credentialJson: credentialJson || undefined,
        settingsJson: settingsJson || '{}',
      };

      const instance = await connectorService.createConnectorInstance(connectorId!, request);
      addNotification('Connector instance created successfully', 'success');
      navigate(`/connectors/instances/${instance.id}`);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to create connector instance';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!connector) {
    return (
      <Box sx={{ p: 6 }}>
        <Alert severity="error">{error || 'Connector not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 6 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(`/connectors/${connectorId}`)} variant="outlined">
          Back
        </Button>
        <ConnectorIcon
          connectorKey={connector.key}
          category={connector.category}
          sx={{ fontSize: 32, color: 'primary.main' }}
        />
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Create {connector.name} Instance
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure a new instance of {connector.name}
          </Typography>
        </Box>
      </Box>

      {/* Progress Stepper */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <LinearProgress
            variant="determinate"
            value={((activeStep + 1) / steps.length) * 100}
            sx={{ mt: 2, height: 4, borderRadius: 2 }}
          />
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Step Content */}
      <Card>
        <CardContent sx={{ p: 4 }}>
          {/* Step 1: Basic Information */}
          {activeStep === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                Basic Information
              </Typography>
              <TextField
                label="Instance Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
                helperText="A unique name to identify this connector instance"
              />
              <TextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={3}
                fullWidth
                helperText="Optional description for this instance"
              />
              <FormControlLabel
                control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                label="Active"
              />
            </Box>
          )}

          {/* Step 2: Authentication */}
          {activeStep === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                Authentication
              </Typography>
              <Alert severity="info">
                Authentication Type: <strong>{connector.authenticationType}</strong>
              </Alert>

              {connector.authenticationType === 'OAuth2' && (
                <Alert severity="info">
                  OAuth connection will be configured after instance creation. You can connect in the instance detail page.
                </Alert>
              )}

              {(connector.authenticationType === 'ApiKey' || connector.authenticationType === 'BearerToken') && (
                <TextField
                  label={connector.authenticationType === 'ApiKey' ? 'API Key' : 'Bearer Token'}
                  type="password"
                  value={credentialJson}
                  onChange={(e) => setCredentialJson(e.target.value)}
                  fullWidth
                  required
                  helperText={`Enter your ${connector.authenticationType === 'ApiKey' ? 'API key' : 'bearer token'}`}
                />
              )}

              {connector.authenticationType === 'BasicAuth' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Username"
                    value={credentialJson ? JSON.parse(credentialJson || '{}').username || '' : ''}
                    onChange={(e) => {
                      const current = credentialJson ? JSON.parse(credentialJson || '{}') : {};
                      setCredentialJson(JSON.stringify({ ...current, username: e.target.value }));
                    }}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Password"
                    type="password"
                    value={credentialJson ? JSON.parse(credentialJson || '{}').password || '' : ''}
                    onChange={(e) => {
                      const current = credentialJson ? JSON.parse(credentialJson || '{}') : {};
                      setCredentialJson(JSON.stringify({ ...current, password: e.target.value }));
                    }}
                    fullWidth
                    required
                  />
                </Box>
              )}

              <ConnectorTestPanel
                instanceId={connectorId!}
                connectorName={connector.name}
                onTestComplete={(result) => {
                  if (result.success) {
                    setIsConnected(true);
                  }
                }}
              />
            </Box>
          )}

          {/* Step 3: Settings & Defaults */}
          {activeStep === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                Settings & Defaults
              </Typography>
              
              {/* Connector-specific configuration (renders specialized config or generic form) */}
              {connector && (
                <ConnectorConfigRenderer
                  connector={connector}
                  instanceId={connectorId}
                  settingsJson={settingsJson}
                  isConnected={isConnected}
                  level="Instance"
                  onChange={setSettingsJson}
                  onConnected={() => setIsConnected(true)}
                />
              )}
              
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Rate Limits
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Per Minute"
                      type="number"
                      value={rateLimitPerMinute}
                      onChange={(e) => setRateLimitPerMinute(e.target.value ? Number(e.target.value) : '')}
                      helperText="Leave empty to inherit from connector defaults"
                    />
                    <TextField
                      label="Per Hour"
                      type="number"
                      value={rateLimitPerHour}
                      onChange={(e) => setRateLimitPerHour(e.target.value ? Number(e.target.value) : '')}
                      helperText="Leave empty to inherit from connector defaults"
                    />
                    <TextField
                      label="Per Day"
                      type="number"
                      value={rateLimitPerDay}
                      onChange={(e) => setRateLimitPerDay(e.target.value ? Number(e.target.value) : '')}
                      helperText="Leave empty to inherit from connector defaults"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Step 4: Capability Selection */}
          {activeStep === 3 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                Capability Selection
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select which capabilities to enable for this instance. Only enabled capabilities will be available as tools.
              </Typography>
              <CapabilityList
                capabilities={connector.capabilities}
                enabledCapabilityIds={enabledCapabilityIds}
                onToggle={handleToggleCapability}
                showDetails
              />
            </Box>
          )}

          {/* Step 5: Review & Create */}
          {activeStep === 4 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                Review & Create
              </Typography>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    Configuration Summary
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Instance Name:
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Status:
                      </Typography>
                      <Chip label={isActive ? 'Active' : 'Inactive'} size="small" color={isActive ? 'success' : 'default'} />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Enabled Capabilities:
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {enabledCapabilityIds.length} / {connector.capabilities.length}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Authentication:
                      </Typography>
                      <Chip
                        label={isConnected ? 'Connected' : 'Not Connected'}
                        size="small"
                        color={isConnected ? 'success' : 'default'}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              variant="outlined"
            >
              Back
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {activeStep < steps.length - 1 ? (
                <Button variant="contained" onClick={handleNext}>
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                  onClick={handleCreate}
                  disabled={saving || !name.trim()}
                >
                  {saving ? 'Creating...' : 'Create Instance'}
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
